import { api } from '../apiMethods'
import { API_ENDPOINTS } from '@/data/constants'
import type { TeamMember, TeamSettings, TeamsApiResponse, RolesApiResponse } from '@/types'

// ============================================================================
// Types for New Team API Endpoints
// ============================================================================

/**
 * Team member from the new /teams/:team_id/members endpoint
 */
export interface TeamMemberApiResponse {
  id: string
  user_id: string
  team_id: string
  email: string
  first_name: string
  last_name: string
  role: {
    id: string
    name: string
    description: string
  }
  status: 'active' | 'inactive' | 'pending' | 'suspended'
  profile_image?: string | null
  joined_at?: string
  last_active_at?: string
  created_at: string
  updated_at: string
}

/**
 * Response from GET /teams/:team_id/members
 */
export interface TeamMembersListResponse {
  success: boolean
  data: TeamMemberApiResponse[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
    hasNext: boolean
    hasPrev: boolean
  }
}

/**
 * Request to add a member to a team
 */
export interface AddTeamMemberData {
  user_id: string
  role_id: string
}

/**
 * Request to update a member's role
 */
export interface UpdateMemberRoleData {
  role: string
}

/**
 * Invitation data for sending team invitations
 */
export interface TeamInvitationData {
  email: string
  role: string
  firstName: string
  lastName: string
}

/**
 * Request to send team invitations
 */
export interface TeamInvitationsRequest {
  invitations: TeamInvitationData[]
}

/**
 * Single invitation request for inviting one member
 */
export interface InviteTeamMemberData {
  email: string
  role_id: string
  first_name?: string
  last_name?: string
}

/**
 * Response from invitation endpoint
 */
export interface TeamInvitationResponse {
  id: string
  team_id: string
  email: string
  first_name?: string
  last_name?: string
  role: string
  status: 'pending' | 'accepted' | 'expired' | 'cancelled'
  invited_at: string
  expires_at: string
  token?: string
}

/**
 * Filters for fetching team members
 */
export interface TeamMemberFilters {
  status?: string
  role?: string
  search?: string
  page?: number
  limit?: number
  sort_by?: string
  sort_order?: string
}

/**
 * Team details from GET /teams/:team_id
 */
export interface TeamDetailsData {
  id: string
  business_id: string
  name: string
  description: string
  status: 'active' | 'inactive' | 'archived'
  settings: Record<string, unknown> | null
  created_at: string
  created_by: string
  updated_at: string
  updated_by: string | null
  is_deleted: boolean
  deleted_at: string | null
  deleted_by: string | null
}

/**
 * Response from GET /teams/:team_id
 */
export interface TeamDetailsResponse {
  status: 'success' | 'error'
  data: {
    team: TeamDetailsData
  }
}

/**
 * Team member from GET /teams/:team_id/members (simplified)
 * Can be either a member or a pending invitation
 */
export interface TeamMemberListItem {
  // Common fields
  email: string
  first_name: string
  last_name: string
  role: string
  status: 'active' | 'inactive' | 'pending' | 'suspended'
  created_at: string
  type: 'member' | 'invitation'

  // Member-specific fields
  user_id?: string
  profile_image?: string | null

  // Invitation-specific fields
  invitation_id?: string
  expires_at?: string
}

/**
 * Response from GET /teams/:team_id/members with pagination
 */
export interface TeamMembersPagedResponse {
  status: 'success' | 'error'
  data: {
    members: TeamMemberListItem[]
    pagination: {
      total: number
      page: number
      limit: number
      total_pages: number
    }
  }
}

// ============================================================================
// Deprecated Types (kept for backwards compatibility during migration)
// ============================================================================

/**
 * @deprecated Use TeamMemberApiResponse instead
 * BusinessUser from the old /business-users/list endpoint
 */
export interface BusinessUser {
  id: string
  first_name: string
  last_name: string
  email: string
  status: string
  is_owner: boolean
  last_login: string
  login_count: number
  email_verified: boolean
  created_at: string
  updated_at: string
  profile_image: string | null
  business: {
    id: string
    name: string
    status: string
  }
  roles: Array<{
    id: string
    name: string
    description: string
  }>
  teams: Array<{ id: string; name: string; role?: string }>
  is_team_member: boolean
}

/**
 * @deprecated Use TeamMembersListResponse instead
 */
export interface BusinessUsersListResponse {
  success: boolean
  data: BusinessUser[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
    hasNext: boolean
    hasPrev: boolean
  }
}

/**
 * @deprecated Use UpdateMemberRoleData instead
 */
export interface UpdateTeamMemberData {
  roleId?: string
  status?: 'active' | 'inactive' | 'suspended' | 'pending'
  permissions?: Record<string, unknown>
}

/**
 * Permission values for a category
 */
export interface RolePermissionCategory {
  [key: string]: boolean
}

/**
 * Master role with permissions
 */
export interface MasterRole {
  id: string
  name: string
  level: number
  scope: 'organization' | 'team'
  description: string
  max_per_org?: number
  permissions: 'ALL' | Record<string, RolePermissionCategory>
  display_name: string
}

/**
 * Response from GET /master-roles-permissions
 */
export interface MasterRolesPermissionsResponse {
  status: 'success' | 'error'
  data: MasterRole[]
}

/**
 * Request to create a new team
 */
export interface CreateTeamData {
  name: string
  description?: string
}

/**
 * Response from POST /teams
 */
export interface CreateTeamResponse {
  status: 'success' | 'error'
  data: {
    team: TeamDetailsData
  }
}

export const teamApi = {
  // ============================================================================
  // Teams
  // ============================================================================

  /**
   * Create a new team
   */
  createTeam: (data: CreateTeamData) => {
    return api.post<CreateTeamResponse>('/teams', data)
  },

  /**
   * Get list of teams
   */
  getTeams: (filters?: { page?: number; limit?: number; status?: string }) => {
    const params = new URLSearchParams()
    if (filters?.page) params.append('page', filters.page.toString())
    if (filters?.limit) params.append('limit', filters.limit.toString())
    if (filters?.status) params.append('status', filters.status)

    const queryString = params.toString()
    return api.get<TeamsApiResponse>(
      `${API_ENDPOINTS.TEAM.LIST}${queryString ? `?${queryString}` : ''}`
    )
  },

  /**
   * Get single team details
   */
  getTeam: (teamId: string) => {
    return api.get<TeamDetailsResponse>(`/teams/${teamId}`)
  },

  /**
   * Get team members with pagination (for team details page)
   * @param teamId - The team ID
   * @param filters - Optional filters for pagination and status
   */
  getTeamMembersPaged: (teamId: string, filters?: { page?: number; limit?: number; status?: string }) => {
    const params = new URLSearchParams()
    if (filters?.page) params.append('page', filters.page.toString())
    if (filters?.limit) params.append('limit', filters.limit.toString())
    if (filters?.status) params.append('status', filters.status)

    const queryString = params.toString()
    return api.get<TeamMembersPagedResponse>(
      `/teams/${teamId}/members${queryString ? `?${queryString}` : ''}`
    )
  },

  // ============================================================================
  // Team Members - New Endpoints
  // ============================================================================

  /**
   * Get team members list
   * @param teamId - The team ID
   * @param filters - Optional filters for pagination, status, role, search
   */
  getMembers: (teamId: string, filters?: TeamMemberFilters) => {
    const params = new URLSearchParams()
    if (filters?.page) params.append('page', filters.page.toString())
    if (filters?.limit) params.append('limit', filters.limit.toString())
    if (filters?.status) params.append('status', filters.status)
    if (filters?.role) params.append('role', filters.role)
    if (filters?.search) params.append('search', filters.search)
    if (filters?.sort_by) params.append('sort_by', filters.sort_by)
    if (filters?.sort_order) params.append('sort_order', filters.sort_order)

    const queryString = params.toString()
    return api.get<TeamMembersListResponse>(
      `/teams/${teamId}/members${queryString ? `?${queryString}` : ''}`
    )
  },

  /**
   * Get single team member
   * @param teamId - The team ID
   * @param userId - The user ID
   */
  getMember: (teamId: string, userId: string) => {
    return api.get<TeamMemberApiResponse>(
      `/teams/${teamId}/members/${userId}`
    )
  },

  /**
   * Add existing user to a team
   * @param teamId - The team ID
   * @param data - User ID and role ID
   */
  addMember: (teamId: string, data: AddTeamMemberData) => {
    return api.post<TeamMemberApiResponse>(
      `/teams/${teamId}/members`,
      data
    )
  },

  /**
   * Update team member's role
   * @param teamId - The team ID
   * @param userId - The user ID
   * @param data - New role ID
   */
  updateMemberRole: (teamId: string, userId: string, data: UpdateMemberRoleData) => {
    return api.put<TeamMemberApiResponse>(
      `/teams/${teamId}/members/${userId}/role`,
      data
    )
  },

  /**
   * Remove member from team
   * @param teamId - The team ID
   * @param userId - The user ID to remove
   */
  removeMember: (teamId: string, userId: string) => {
    return api.delete<void>(
      `/teams/${teamId}/members/${userId}`
    )
  },

  // ============================================================================
  // Team Invitations - New Endpoints
  // ============================================================================

  /**
   * Send invitation to join a team
   * @param teamId - The team ID
   * @param data - Invitation data (email, role, name)
   */
  inviteMember: (teamId: string, data: InviteTeamMemberData) => {
    return api.post<TeamInvitationResponse>(
      `/teams/${teamId}/invitations`,
      data
    )
  },

  /**
   * Send multiple team invitations (for onboarding)
   * @param teamId - The team ID
   * @param data - Array of invitations
   */
  sendTeamInvitations: (teamId: string, data: TeamInvitationsRequest) => {
    return api.post<TeamInvitationResponse[]>(
      `/teams/${teamId}/invitations`,
      data
    )
  },

  /**
   * Get pending invitations for a team
   * @param teamId - The team ID
   * @param filters - Optional filters
   */
  getInvitations: (teamId: string, filters?: { page?: number; limit?: number; status?: string; sort_by?: string; sort_order?: string }) => {
    const params = new URLSearchParams()
    if (filters?.page) params.append('page', filters.page.toString())
    if (filters?.limit) params.append('limit', filters.limit.toString())
    if (filters?.status) params.append('status', filters.status)
    if (filters?.sort_by) params.append('sort_by', filters.sort_by)
    if (filters?.sort_order) params.append('sort_order', filters.sort_order)

    const queryString = params.toString()
    return api.get<TeamInvitationResponse[]>(
      `/teams/${teamId}/invitations${queryString ? `?${queryString}` : ''}`
    )
  },

  /**
   * Revoke/cancel an invitation
   * @param invitationId - The invitation ID
   */
  revokeInvitation: (invitationId: string) => {
    return api.delete<void>(
      `/teams/invitations/${invitationId}`
    )
  },

  /**
   * Resend an invitation
   * @param invitationId - The invitation ID
   */
  resendInvitation: (invitationId: string) => {
    return api.post<TeamInvitationResponse>(
      `/teams/invitations/${invitationId}/resend`
    )
  },

  // ============================================================================
  // Roles
  // ============================================================================

  /**
   * Get team roles
   */
  getRoles: (page = 1, limit = 20) => {
    const params = new URLSearchParams()
    params.append('page', page.toString())
    params.append('limit', limit.toString())

    return api.get<RolesApiResponse>(
      `${API_ENDPOINTS.TEAM.ROLES}?${params.toString()}`
    )
  },

  /**
   * Get master roles with permissions matrix
   */
  getMasterRolesPermissions: () => {
    return api.get<MasterRolesPermissionsResponse>('/master-roles-permissions')
  },

  // ============================================================================
  // Team Settings
  // ============================================================================

  /**
   * Get team settings
   * @param teamId - The team ID
   */
  getSettings: (teamId: string) => {
    return api.get<TeamSettings>(
      `/teams/${teamId}/settings`
    )
  },

  /**
   * Update team settings
   * @param teamId - The team ID
   * @param data - Settings to update
   */
  updateSettings: (teamId: string, data: Partial<TeamSettings>) => {
    return api.put<TeamSettings>(
      `/teams/${teamId}/settings`,
      data
    )
  },

  // ============================================================================
  // Deprecated Methods (kept for backwards compatibility during migration)
  // ============================================================================

  /**
   * @deprecated Use getMembers(teamId, filters) instead
   * Get team members using the old business-users endpoint
   */
  getMembersLegacy: (_filters?: TeamMemberFilters) => {
    return api.get<BusinessUsersListResponse>('/business-users/list')
  },

  /**
   * @deprecated Use removeMember(teamId, userId) instead
   * Remove member using the old business-users endpoint
   */
  removeMemberLegacy: (memberId: string) => {
    return api.delete<{ success: boolean }>(`/business-users/${memberId}`)
  },

  /**
   * @deprecated Use revokeInvitation instead
   * Cancel invitation using the old endpoint
   */
  cancelInvitation: (invitationId: string) => {
    return api.delete<{ success: boolean }>(`/teams/invitations/${invitationId}`)
  },

  /**
   * @deprecated Use getInvitations(teamId, filters) instead
   * Get pending invitations using the old business-users endpoint
   */
  getPendingInvitationsLegacy: (_filters?: { page?: number; limit?: number; status?: string; sort_by?: string; sort_order?: string }) => {
    return api.get<BusinessUsersListResponse>('/business-users/list')
  },

  /**
   * @deprecated Use updateMemberRole instead
   * Update team member using the old endpoint
   */
  updateMember: (memberId: string, data: UpdateTeamMemberData) => {
    return api.put<TeamMember>(`${API_ENDPOINTS.TEAM.UPDATE}/${memberId}`, data)
  },
}